<template>
  <div class="cb-page profile-screen">
    <div class="profile-screen__top">
      <button v-if="showBack" type="button" class="profile-screen__back" aria-label="Назад" @click="goBack">
        <AppIcon name="chevron-left" :size="26" />
      </button>
      <h1 class="cb-sr-only">Профиль</h1>
      <StoragePill v-if="session.me" class="profile-screen__pill" variant="glow" :count="session.stars" />
      <span v-else class="profile-screen__pill-skeleton cb-skeleton" aria-hidden="true"></span>
    </div>

    <ProfileRetryCard v-if="profileFailed" />
    <ProfileCard v-else :player="session.me" editable avatar-editable @edit="openRename" @edit-avatar="openAvatar" />

    <GlassCard class="profile-settings" padding="none" radius="xl" tag="section" aria-label="Настройки профиля">
      <div class="profile-settings__row">
        <span class="profile-settings__label">Фото профиля</span>
        <span
          v-if="session.me"
          class="profile-settings__value cb-ellipsis"
          :class="{ 'profile-settings__value--empty': !session.me.avatarUrl }"
        >
          {{ avatarSourceText }}
        </span>
        <span v-else class="profile-settings__value-skeleton cb-skeleton"></span>
        <AppButton
          variant="outline"
          size="sm"
          class="profile-settings__btn"
          :disabled="!session.me"
          aria-label="Сменить фото профиля"
          @click="openAvatar"
        >
          Сменить
        </AppButton>
      </div>

      <div class="profile-settings__row">
        <span class="profile-settings__label">Имя в игре</span>
        <span v-if="session.me" class="profile-settings__value cb-ellipsis">{{ session.me.playerName }}</span>
        <span v-else class="profile-settings__value-skeleton cb-skeleton"></span>
        <AppButton
          variant="outline"
          size="sm"
          class="profile-settings__btn"
          :disabled="!session.me"
          aria-label="Сменить имя в игре"
          @click="openRename"
        >
          Сменить
        </AppButton>
      </div>

      <div class="profile-settings__row">
        <span class="profile-settings__label">Телефон / Email</span>
        <span v-if="session.me" class="profile-settings__value cb-ellipsis" :class="{ 'profile-settings__value--empty': !contact }">
          {{ contact || "Не указан" }}
        </span>
        <span v-else class="profile-settings__value-skeleton cb-skeleton"></span>
        <AppButton
          variant="outline"
          size="sm"
          class="profile-settings__btn"
          aria-label="Сменить телефон или email"
          @click="soon"
        >
          Сменить
        </AppButton>
      </div>

      <div class="profile-settings__row">
        <span class="profile-settings__label">Ссылка на&nbsp;приглашение</span>
        <span v-if="invite" class="profile-settings__value profile-settings__invite">
          <span class="cb-ellipsis">{{ inviteShort }}</span>
          <button type="button" class="profile-settings__copy" aria-label="Скопировать ссылку-приглашение" @click="copyInvite">
            <AppIcon name="copy" :size="20" :stroke-width="1.8" />
          </button>
        </span>
        <span v-else class="profile-settings__value-skeleton cb-skeleton"></span>
        <AppButton
          variant="accent"
          size="sm"
          class="profile-settings__btn profile-settings__btn--tall"
          :disabled="!invite"
          @click="shareInvite"
        >
          Ссылка на приглашение
        </AppButton>
      </div>

      <div class="profile-settings__row">
        <span class="profile-settings__label">Настройки уведомлений</span>
        <span class="profile-settings__value cb-ellipsis">
          {{ session.notificationsEnabled ? "Включены" : "Выключены" }}
        </span>
        <AppButton
          variant="outline"
          size="sm"
          class="profile-settings__btn"
          :aria-label="session.notificationsEnabled ? 'Выключить уведомления' : 'Включить уведомления'"
          @click="toggleNotifications"
        >
          Сменить
        </AppButton>
      </div>
    </GlassCard>

    <GlassCard class="profile-balance" padding="none" radius="xl" tag="section" aria-label="Баланс звёзд">
      <div class="profile-balance__body">
        <BalanceStar class="profile-balance__star" />
        <div class="profile-balance__texts">
          <span class="profile-balance__label">Баланс</span>
          <span v-if="session.me" class="profile-balance__count cb-num" :class="countClass">{{ starsText }}</span>
          <span v-else class="profile-balance__count-skeleton cb-skeleton" aria-hidden="true"></span>
          <span class="profile-balance__you">У вас!</span>
        </div>
      </div>
      <div class="profile-balance__action">
        <AppButton block class="profile-balance__btn" @click="soon">Купить/продать</AppButton>
      </div>
    </GlassCard>

    <AppButton variant="secondary" size="md" icon="info" block class="profile-screen__rules" @click="openRules">
      Правила
    </AppButton>

    <AppButton variant="ghost" size="md" block @click="router.push('/diagnostics')">
      Диагностика
    </AppButton>

    <BottomSheet v-model="renameOpen" title="Имя в игре" :persistent="saving">
      <form class="rename-form" novalidate @submit.prevent="saveName">
        <label class="rename-form__label" for="profile-rename-input">Как вас будут видеть соперники</label>
        <input
          id="profile-rename-input"
          v-model="nameDraft"
          class="cb-input rename-form__input"
          :class="{ 'cb-input--error': !!nameError }"
          type="text"
          maxlength="24"
          autocomplete="off"
          autocapitalize="words"
          spellcheck="false"
          enterkeyhint="done"
          placeholder="Например, Ваня777"
          :aria-invalid="nameError ? 'true' : 'false'"
          aria-describedby="profile-rename-hint"
          autofocus
          @input="nameError = ''"
        />
        <div class="rename-form__meta">
          <span
            id="profile-rename-hint"
            class="rename-form__hint"
            :class="{ 'rename-form__hint--error': !!nameError }"
            :role="nameError ? 'alert' : undefined"
          >
            {{ nameError || NAME_HINT }}
          </span>
          <span class="rename-form__counter cb-num" :class="{ 'rename-form__counter--over': trimmedName.length > NAME_MAX }">
            {{ trimmedName.length }}/{{ NAME_MAX }}
          </span>
        </div>
      </form>
      <template #footer>
        <AppButton block :loading="saving" :disabled="!trimmedName" @click="saveName">Сохранить</AppButton>
      </template>
    </BottomSheet>

    <BottomSheet v-model="avatarOpen" title="Фото профиля" :persistent="avatarBusy">
      <div class="avatar-form">
        <PlayerAvatar
          :src="avatarDraft ? avatarDraft.url : session.me && session.me.avatarUrl"
          :name="session.me ? session.me.playerName : ''"
          :size="168"
          :ring="!avatarDraft"
          :glow="!!avatarDraft"
        />
        <p
          class="avatar-form__hint"
          :class="{ 'avatar-form__hint--error': !!avatarError }"
          :role="avatarError ? 'alert' : undefined"
        >
          {{ avatarError || (avatarDraft ? "Так вас увидят соперники" : AVATAR_HINT) }}
        </p>
        <input ref="avatarInput" type="file" accept="image/*" hidden @change="onAvatarPicked" />
      </div>
      <template #footer>
        <div class="avatar-form__actions">
          <template v-if="avatarDraft">
            <AppButton block :loading="avatarSaving" :disabled="avatarBusy && !avatarSaving" @click="saveAvatar">
              Сохранить
            </AppButton>
            <AppButton variant="ghost" size="md" block :disabled="avatarBusy" @click="pickAvatar">
              Выбрать другое
            </AppButton>
          </template>
          <template v-else>
            <AppButton block icon="camera" :loading="avatarPreparing" :disabled="avatarBusy && !avatarPreparing" @click="pickAvatar">
              Выбрать фото
            </AppButton>
            <AppButton
              v-if="session.me && session.me.customAvatar"
              variant="ghost"
              size="md"
              block
              :loading="avatarResetting"
              :disabled="avatarBusy && !avatarResetting"
              @click="resetAvatar"
            >
              Убрать своё фото
            </AppButton>
          </template>
        </div>
      </template>
    </BottomSheet>
  </div>
</template>

<script setup>
/**
 * ProfileScreen (/profile) — «Профиль» (макет 10).
 *
 * Хранилище, карточка профиля, настройки: фото (BottomSheet → utils/image.prepareAvatar →
 * session.updateAvatar / update-player-avatar, «Убрать своё фото» → reset-player-avatar),
 * имя (BottomSheet → session.rename / update-player-name),
 * телефон/email («Скоро»), ссылка-приглашение (копирование + шаринг через Telegram), уведомления
 * (локальная настройка), баланс звёзд («Купить/продать» — «Скоро»), «Правила».
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import AppButton from "@/components/ui/AppButton.vue";
import AppIcon from "@/components/ui/AppIcon.vue";
import BottomSheet from "@/components/ui/BottomSheet.vue";
import GlassCard from "@/components/ui/GlassCard.vue";
import PlayerAvatar from "@/components/ui/PlayerAvatar.vue";
import ProfileCard from "@/components/ui/ProfileCard.vue";
import StoragePill from "@/components/ui/StoragePill.vue";
import BalanceStar from "./main/BalanceStar.vue";
import ProfileRetryCard from "./main/ProfileRetryCard.vue";
import { useSessionStore } from "@/stores/session";
import { useToast } from "@/composables/useToast";
import { inviteLink } from "@/utils/player";
import { copyText, formatNumber } from "@/utils/format";
import { prepareAvatar } from "@/utils/image";
import telegram from "@/services/telegram";

const NAME_MIN = 2;
const NAME_MAX = 24;
const NAME_HINT = "2–24 символа: буквы, цифры, пробел и _ - .";
const SHARE_TEXT = "Сыграем в «Гляделки»? Кто первым моргнёт — проиграл!";
const AVATAR_HINT = "Фото обрежется до квадрата по центру";

/** Как на сервере: буквы (любой алфавит), цифры, пробел, «_», «-», «.». */
const NAME_RE = (() => {
  try {
    return new RegExp("^[\\p{L}\\p{N} _.-]+$", "u");
  } catch (e) {
    // движок без Unicode property escapes — латиница и кириллица
    return /^[0-9A-Za-zА-Яа-яЁё _.-]+$/;
  }
})();

const router = useRouter();
const session = useSessionStore();
const toast = useToast();

/** Вне Telegram нет системной «Назад» — показываем свою кнопку. */
const showBack = !telegram.isTelegram;

/* ------------------------------------------------------------ данные */

/** Профиль так и не загрузился — вместо скелетона карточка «Повторить». */
const profileFailed = computed(() => !session.me && !!session.meError && !session.meLoading);

const contact = computed(() => (session.me && session.me.username) || "");

const avatarSourceText = computed(() => {
  if (!session.me) return "";
  if (session.me.customAvatar) return "Своё фото";
  return session.me.avatarUrl ? "Из Telegram" : "Не задано";
});

const invite = computed(() => (session.userId !== null && session.userId !== undefined ? inviteLink(session.userId) : ""));
const inviteShort = computed(() => invite.value.replace(/^https?:\/\//, ""));

const starsText = computed(() => formatNumber(session.stars));
const countClass = computed(() => {
  const len = starsText.value.length;
  if (len >= 6) return "profile-balance__count--xl";
  if (len >= 4) return "profile-balance__count--l";
  return "";
});

/* ---------------------------------------------------------- действия */

function goBack() {
  const back = window.history.state && window.history.state.back;
  if (back) router.back();
  else router.push("/");
}

function openRules() {
  router.push("/rules");
}

function soon() {
  toast.info("Скоро");
}

async function copyInvite() {
  if (!invite.value) return;
  const ok = await copyText(invite.value);
  if (ok) toast.success("Ссылка скопирована");
  else toast.error("Не удалось скопировать ссылку");
}

async function shareInvite() {
  if (!invite.value) return;
  telegram.haptic("light");
  const result = await telegram.shareUrl(invite.value, SHARE_TEXT);
  if (result === "copied") toast.success("Ссылка скопирована");
  else if (result === "failed" && (await copyText(invite.value))) toast.success("Ссылка скопирована");
}

function toggleNotifications() {
  const enabled = session.toggleNotifications();
  telegram.haptic("selection");
  toast.info(enabled ? "Уведомления включены" : "Уведомления выключены");
}

/* ------------------------------------------------------- смена имени */

const renameOpen = ref(false);
const nameDraft = ref("");
const nameError = ref("");
const saving = ref(false);

const trimmedName = computed(() => nameDraft.value.trim());

function openRename() {
  if (!session.me) return;
  nameDraft.value = session.me.playerName || "";
  nameError.value = "";
  renameOpen.value = true;
}

function validateName(name) {
  if (name.length < NAME_MIN) return "Имя должно быть не короче 2 символов";
  if (name.length > NAME_MAX) return "Имя должно быть не длиннее 24 символов";
  if (!NAME_RE.test(name)) return "Можно только буквы, цифры, пробел и символы «_», «-», «.»";
  return "";
}

async function saveName() {
  if (saving.value) return;
  const name = trimmedName.value;
  const error = validateName(name);
  if (error) {
    nameError.value = error;
    telegram.haptic("error");
    return;
  }
  if (session.me && name === session.me.playerName) {
    renameOpen.value = false;
    return;
  }
  saving.value = true;
  try {
    await session.rename(name);
    renameOpen.value = false;
    toast.success("Имя сохранено");
  } catch (e) {
    nameError.value = (e && e.message) || "Не удалось сохранить имя";
  } finally {
    saving.value = false;
  }
}

/* ------------------------------------------------------- фото профиля */

const avatarOpen = ref(false);
const avatarInput = ref(null);
/** Выбранное, но ещё не сохранённое фото: { blob, url } (url — object URL для превью). */
const avatarDraft = ref(null);
const avatarError = ref("");
const avatarPreparing = ref(false);
const avatarSaving = ref(false);
const avatarResetting = ref(false);

const avatarBusy = computed(() => avatarPreparing.value || avatarSaving.value || avatarResetting.value);

function clearAvatarDraft() {
  if (avatarDraft.value) URL.revokeObjectURL(avatarDraft.value.url);
  avatarDraft.value = null;
}

function openAvatar() {
  if (!session.me) return;
  clearAvatarDraft();
  avatarError.value = "";
  avatarOpen.value = true;
}

function pickAvatar() {
  if (avatarBusy.value || !avatarInput.value) return;
  avatarInput.value.value = ""; // повторный выбор того же файла тоже должен вызвать change
  avatarInput.value.click();
}

async function onAvatarPicked(event) {
  const file = event.target.files && event.target.files[0];
  if (!file) return;
  avatarError.value = "";
  avatarPreparing.value = true;
  try {
    const blob = await prepareAvatar(file);
    clearAvatarDraft();
    avatarDraft.value = { blob, url: URL.createObjectURL(blob) };
  } catch (e) {
    avatarError.value = (e && e.message) || "Не удалось открыть изображение";
    telegram.haptic("error");
  } finally {
    avatarPreparing.value = false;
  }
}

async function saveAvatar() {
  if (!avatarDraft.value || avatarBusy.value) return;
  avatarError.value = "";
  avatarSaving.value = true;
  try {
    await session.updateAvatar(avatarDraft.value.blob);
    avatarOpen.value = false;
    toast.success("Фото сохранено");
  } catch (e) {
    avatarError.value = (e && e.message) || "Не удалось сохранить фото";
    telegram.haptic("error");
  } finally {
    avatarSaving.value = false;
  }
}

async function resetAvatar() {
  if (avatarBusy.value) return;
  avatarError.value = "";
  avatarResetting.value = true;
  try {
    await session.resetAvatar();
    avatarOpen.value = false;
    toast.success("Своё фото убрано");
  } catch (e) {
    avatarError.value = (e && e.message) || "Не удалось убрать фото";
    telegram.haptic("error");
  } finally {
    avatarResetting.value = false;
  }
}

watch(avatarOpen, (open) => {
  if (!open) clearAvatarDraft();
});

onBeforeUnmount(clearAvatarDraft);

onMounted(() => {
  // Свежий баланс и статистика (ошибку покажет скелетон/повторный вход; 401 — «Сессия истекла»).
  session.loadMe().catch(() => null);
});
</script>

<style scoped>
.profile-screen {
  gap: 14px;
}

/* ------------------------------------------------------------------- top */

.profile-screen__top {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  min-height: 64px;
  padding-top: 4px;
}

.profile-screen__back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  margin-right: auto;
  padding: 0;
  border-radius: 50%;
  border: 1px solid var(--cb-border-strong);
  background: linear-gradient(180deg, var(--cb-surface-2) 0%, var(--cb-surface) 100%);
  color: var(--cb-text);
  box-shadow: var(--cb-inner-highlight);
  cursor: pointer;
}

.profile-screen__back:active {
  transform: scale(0.95);
  border-color: var(--cb-lime-border);
}

.profile-screen__pill {
  flex-shrink: 0;
}

.profile-screen__pill-skeleton {
  width: 136px;
  height: 62px;
  border-radius: var(--cb-radius-md);
}

/* -------------------------------------------------------------- settings */

.profile-screen .profile-settings {
  padding: 6px 14px;
}

.profile-settings__row {
  position: relative;
  display: grid;
  grid-template-columns: minmax(84px, 32%) minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 10px;
  min-height: 66px;
  padding: 10px 0;
}

.profile-settings__row + .profile-settings__row::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--cb-border);
}

.profile-settings__label {
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  color: var(--cb-text-2);
  overflow-wrap: break-word;
  -webkit-hyphens: auto;
  hyphens: auto;
}

.profile-settings__value {
  min-width: 0;
  font-size: 14.5px;
  font-weight: 500;
  color: var(--cb-text);
}

.profile-settings__value--empty {
  color: var(--cb-text-3);
}

.profile-settings__value-skeleton {
  display: block;
  width: 80%;
  height: 16px;
}

.profile-settings__invite {
  display: flex;
  align-items: center;
  gap: 2px;
}

.profile-settings__copy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--cb-text);
  cursor: pointer;
}

.profile-settings__copy:active {
  background: var(--cb-lime-soft);
  color: var(--cb-lime);
}

.profile-settings .profile-settings__btn {
  width: 104px;
  height: 46px;
  padding: 0 8px;
  border-radius: var(--cb-radius-sm);
  font-size: 14px;
  font-weight: 700;
}

.profile-settings .profile-settings__btn--tall {
  height: auto;
  min-height: 54px;
  padding: 6px 6px;
  font-size: 12.5px;
  line-height: 1.2;
}

/* --------------------------------------------------------------- balance */

.profile-screen .profile-balance {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  background:
    radial-gradient(70% 90% at 30% 45%, rgba(221, 245, 90, 0.08) 0%, rgba(221, 245, 90, 0) 70%),
    linear-gradient(180deg, var(--cb-surface-2) 0%, var(--cb-surface) 100%);
}

.profile-balance__body {
  display: flex;
  align-items: center;
  padding: 14px 16px 6px 8px;
}

.profile-balance__star {
  --bs-size: min(50vw, 216px);
}

.profile-balance__texts {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  margin-left: -14px;
}

.profile-balance__label {
  margin-bottom: 2px;
  padding-left: 4px;
  font-size: 16px;
  font-weight: 500;
  color: var(--cb-text-2);
}

.profile-balance__count {
  font-size: clamp(60px, 20vw, 88px);
  font-weight: 800;
  line-height: 0.95;
  letter-spacing: -0.04em;
  color: #FFFFFF;
  text-shadow: 0 4px 18px rgba(0, 0, 0, 0.5), 0 0 1px rgba(255, 255, 255, 0.4);
  white-space: nowrap;
}

.profile-balance__count--l {
  font-size: clamp(40px, 12vw, 58px);
}

.profile-balance__count--xl {
  font-size: clamp(30px, 9vw, 42px);
}

.profile-balance__count-skeleton {
  width: 80px;
  height: 64px;
  margin: 6px 0;
  border-radius: 14px;
}

.profile-balance__you {
  margin-top: 4px;
  font-size: clamp(32px, 10.6vw, 46px);
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
  color: var(--cb-lime);
  text-shadow: var(--cb-text-glow);
  white-space: nowrap;
}

.profile-balance__action {
  padding: 4px 8px 8px;
}

.profile-balance__action .profile-balance__btn {
  height: 62px;
  font-size: 21px;
}

/* ----------------------------------------------------------------- rules */

.profile-screen .profile-screen__rules {
  height: 56px;
  font-size: 18px;
}

/* ---------------------------------------------------------------- rename */

.rename-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rename-form__label {
  font-size: 14px;
  font-weight: 500;
  color: var(--cb-text-2);
}

.rename-form__input {
  font-size: 18px;
  font-weight: 600;
}

.rename-form__meta {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  min-height: 20px;
}

.rename-form__hint {
  font-size: 13px;
  line-height: 1.4;
  color: var(--cb-text-3);
}

.rename-form__hint--error {
  color: var(--cb-danger);
}

.rename-form__counter {
  flex-shrink: 0;
  font-size: 13px;
  color: var(--cb-text-3);
}

.rename-form__counter--over {
  color: var(--cb-danger);
}

/* ---------------------------------------------------------------- avatar */

.avatar-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 8px 0 4px;
}

.avatar-form__hint {
  margin: 0;
  min-height: 20px;
  font-size: 14px;
  line-height: 1.4;
  text-align: center;
  color: var(--cb-text-2);
}

.avatar-form__hint--error {
  color: var(--cb-danger);
}

.avatar-form__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

@media (max-width: 370px) {
  .profile-settings__row {
    grid-template-columns: minmax(76px, 28%) minmax(0, 1fr) auto;
    column-gap: 8px;
  }

  .profile-settings .profile-settings__btn {
    width: 94px;
    font-size: 13px;
  }

  .profile-settings .profile-settings__btn--tall {
    font-size: 11.5px;
  }

  .profile-settings__label {
    font-size: 12.5px;
  }
}
</style>
